<template>
<div class="container-fluid">

<toolbar>
	<h2>Form Template Editor</h2>
</toolbar>

<div class="row" v-if="formTemplate.length > 0">
	<div class="col-lg-8 col-md-8">
		<div class="d-flex flex-row justify-content-between align-items-center mb-2">
			<router-link :to="backLink">
				<i class="fa fa-arrow-left"></i>
				Back to templates list
			</router-link>

			<button class="btn btn-success" v-on:click="saveTemplate">
				<i class="fa fa-save"></i>
				Save
			</button>
		</div>

		<field label="Template Name"
			col-class="col-md-4"
			v-model="template.title"
			:invalid="$store.errorList.title">
		</field>

		<field label="Template Category"
			col-class="col-md-4"
			v-model="template.category_id"
			:invalid="$store.errorList.category_id"
			:params="{type:'select',options:categoryOptions}">
		</field>

		<field label="Role Permissions"
			col-class="col-md-4"
			v-model="permissionRoles"
			:params="{type:'select-multi',options:rolesOptions}">
			<div class="text-muted">If roles are assigned here, only a user with one of these roles on the project/service can create a form from this template</div>
		</field>

		<field label="Is Single Use Form"
			col-class="col-md-4"
			v-model="template.is_single_use"
			:params="{type:'boolean'}">
			<div class="text-muted">Single use forms do not stay open for additional participants to review/acknowledge the form after it has been filled out/submitted</div>
		</field>

		<field v-if="template.is_single_use == 0"
			label="Expiry Interval"
			col-class="col-md-4"
			v-model="template.expiry_interval"
			:params="{type:'select',options:[
				{value: 0,text:'Doesn\'t Expire'},
				{value: 16,text:'16 hours'},
				{value: 20,text:'20 hours'},
				{value: 24,text:'24 hours'},
				{value: 48,text:'2 days'},
				{value: 72,text:'3 days'},
				{value: 120,text:'5 days'},
				{value: 168,text:'7 days'}
			]}">
			<div class="text-muted">If an expiry is set, supervisors will need to "initiate" a Form in order to make it available, and then it will only be available for the set interval, after which they will need to re-initate the form again.</div>
		</field>	

		<field label="This is an equipment-related form"
			col-class="col-md-4"
			:params="{type:'boolean'}"
			v-model="template.has_equipment">
		</field>

		<field v-if="template.has_equipment == 1"
			label="Applicable Equipment Types"
			col-class="col-md-4"
			:params="{type:'select-multi',options:equipmentTypeOptions}"
			v-model="applicableEquipmentTypes">
			<span class="text-muted">Leave blank to make it applicable to all equipment types</span>
		</field>	

		<field label="Is a form for Service Worksheets"
			col-class="col-md-4"
			v-model="template.is_worksheet_form"
			:params="{type:'boolean'}">
			<div class="text-muted">Service Worksheet forms will be the only forms that are accessible when adding a form for a Service Worksheet Entry</div>
		</field>

		<draggable v-model="formTemplate" handle=".handle-section">
			<form-builder-section  v-for="(section,sectionIndex) in formTemplate"
				:key="'section'+sectionIndex"
				:is-active="sectionIndex == activeSectionIndex"
				:job-personnel-roles="$store.jobPersonnelRoles"
				:employees="employees"
				v-on:trash="trashSection(sectionIndex)"
				v-on:activate="activeSectionIndex = sectionIndex"
				v-model="formTemplate[sectionIndex]">
			</form-builder-section>
		</draggable>

		<div class="d-flex flex-row justify-content-end align-items-center mb-2">
			<button class="btn btn-success" v-on:click="saveTemplate">
				<i class="fa fa-save"></i>
				Save
			</button>
		</div>
	</div>
	<div class="col-lg-4 col-md-4">
		<button v-if="!showFieldButtons" class="btn btn-light mb-1" v-on:click="showFieldButtons = true" style="width: 100%;">
			<i class="fa fa-plus"></i>
			Add a Field
		</button>
		<div v-else class="add-controls d-flex flex-column mb-2">
			<div class="d-flex justify-content-between">
				<h3>Add Field to <em>{{ formTemplate[activeSectionIndex].sectionHeading }}</em> Section</h3>
				<i class="fa fa-times icon-button" v-on:click="showFieldButtons = false"></i>
			</div>

			<button class="btn btn-light d-flex align-items-center" v-for="typeButton in controlTypes" v-on:click="addField(typeButton.value)">
				<i class="fa-fw" :class="typeButton.icon"></i>
				<span class="flex-grow-1">
					{{typeButton.text}}
				</span>
			</button>
		</div>

		<button class="btn btn-light" v-on:click="newSection" style="width: 100%;">
			<i class="fa fa-plus"></i>
			Add a Section
		</button>

		<p class="mt-2"><a href="#" v-on:click.prevent="deleteTemplate" class="text-danger">Delete Template...</a></p>

		<article class="preview-panel">
			<h3>Form Preview</h3>

			<section v-for="(section,index) in formTemplate">
				<h4>{{index+1}}. {{section.sectionHeading}}</h4>

				<div v-for="q in section.fields" class="section-question">
					<h5>{{q.label}}</h5>

					<div v-if="q.type == 'static-paragraph'" v-html="stripInlineEventHandlers(q.default)" class="text-muted"></div>
				</div>
			</section>
		</article>
	</div>

	<modal-dialog :modal-visible="deleteModal.visible"
		title="Delete Satety Form Template"
		confirm-button-text="Yes, Delete"
		button-class="btn-outline-danger"
		v-on:closeModal="deleteModal.visible = false"
		v-on:confirm="confirmDeleteTemplate">

		Are you sure you want to delete this Form Template?

	</modal-dialog>

</div>
</div>
</template>
<script type="text/javascript">
import FormBuilderSection from "@/components/FormBuilderSection"
import Field from "@/components/Field"
import draggable from 'vuedraggable'
import Toolbar from "@/layout_fragments/Toolbar"

import ModalDialog from "@/components/ModalDialog"

import FormMixins from "@/mixins/FormMixins.js"

export default {
	components: {
		FormBuilderSection,
		Field,
		draggable,
		ModalDialog,
		Toolbar
	},
	mixins: [FormMixins],
	data() {
		return {
			template: {},
			applicableEquipmentTypes: [],
			formTemplate: [],
			activeSectionIndex: 0,
			personnelRoles: [],
			employees: [],
			permissionRoles: [],
			showFieldButtons: false,
			controlTypes: [
				{text: "Text",                value:'text',                 icon:'fa fa-pencil-alt'},
				{text: "Textarea",            value:'textarea',             icon:'fa fa-pen-square'},
				{text: "Date",                value:'date',                 icon:'fa fa-calendar-alt'},
				{text: "Time",                value:'time',                 icon:'fa fa-clock'},
				{text: "Yes/No (boolean)",    value:'boolean',              icon:'fa fa-check-square'},
				{text: "Select",              value:'select',               icon:'fa fa-caret-square-down'},
				{text: "Multiple Select",     value:'select-multi',         icon:'fa fa-caret-square-down'},
				{text: "Static Paragraph",    value:'static-paragraph',     icon:'fa fa-align-justify'},
				{text: "Image",               value:'image',                icon:'fa fa-image'},
				{text: "Whiteboard",          value:'whiteboard',           icon:'fa fa-chalkboard'},
				{text: "Weather Widget",      value:'weather',              icon:'fa fa-cloud'},
				{text: "Task List",           value:'tasklist',             icon:'fa fa-list'},
				{text: "Personnel Phone List",value:'personnel-phone-list', icon:'fa fa-address-book'},
				{text: "Table",               value:'table',                icon:'fa fa-table'},
				{text: "Files",        		  value:'library-file',         icon:'fa fa-book'},
				{text: "YouTube Video",       value:'youtube-video',        icon:'fab fa-youtube'}
			],
			deleteModal: {
				visible: false
			}
		}
	},
	computed: {
		rolesOptions() {
			return this.optionify(this.personnelRoles,"title")
		},
		categoryOptions() {
			return this.optionify(this.$store.companyFormTemplateCategories,"name")
		},
		backLink() {
			if ("cat_slug" in this.$route.params) {
				let cat = this.$store.companyFormTemplateCategories.find(cat => cat.name.slugify() == this.$route.params.cat_slug)

				if (cat)
					return "/forms/templates/"+cat.name.slugify()
			}
			
			return "/forms/templates"
		}
	},
	methods: {
		setCategoryFromUrl() {
			if ("cat_slug" in this.$route.params) {
				let cat = this.$store.companyFormTemplateCategories.find(cat => cat.name.slugify() == this.$route.params.cat_slug)
				
				if (cat)
					this.$set(this.template,"category_id",cat.id)
			}
		},
		trashSection(index) {
			this.formTemplate.splice(index,1)
		},
		addField(type) {
			this.formTemplate[this.activeSectionIndex].fields.push({
				type: type,
				label: "[New Field]"
			})
			this.alertSuccess("New field added to "+this.formTemplate[this.activeSectionIndex].sectionHeading+" section")
		},
		newSection() {
			this.formTemplate.splice(this.activeSectionIndex+1,0,{
				sectionHeading: "[New Section]",
				fields: [
					{
						label: "[New Field]",
						type: "text"
					}
				]
			})
			this.activeSectionIndex++
		},
		deleteTemplate() {
			this.deleteModal.visible = true
		},
		confirmDeleteTemplate() {
			let vm = this

			this.postData("FormTemplate/delete",{id: this.template.id},true).then(response => {
				if (response.success) {
					vm.alertSuccess("Form template deleted")
					vm.$router.push("/forms/templates")
				}
			})
		},
		saveTemplate() {
			let vm = this

			let payload = {
				title: this.template.title,
				expiry_interval: this.template.expiry_interval,
				template_json: JSON.stringify(this.formTemplate),
				category_id: this.template.category_id,
				is_single_use: this.template.is_single_use,
				is_worksheet_form: this.template.is_worksheet_form,
				JobPersonnelRole: this.permissionRoles
			}

			if ("id" in this.template && this.template.id)
				payload.id = this.template.id

			if (this.template.has_equipment == 1) {
				payload.has_equipment = 1
				payload.EquipmentType = this.applicableEquipmentTypes
			}
			else
				payload.has_equipment = 0

			this.postData("FormTemplate/save",payload,true).then(response => {
				if (response.success) {
					let url = vm.segmentUrl(window.location.href)					

					if (url[url.length-1] == 'new') {
						window.history.replaceState({},{},"/forms/templates/"+response.slug)
						vm.template.id = response.data.record_id

					}

					vm.alertSaved("Template saved!")
				}
			})
		},
		fetchTemplateExtended() {
			let vm = this

			if (this.$route.params.slug != 'new') {
				this.fetchTemplate(this.$route.params.slug).then(response => {
					if (response.success) {
						vm.template = response.data

						vm.formTemplate = ""
						try {
							vm.formTemplate = JSON.parse(vm.template.template_json)
						}
						catch (e) {}

						vm.activeSectionIndex = 0

						vm.applicableEquipmentTypes = vm.primitize(response.data.EquipmentType)

						vm.permissionRoles = response.data.JobPersonnelRole.map(role => +role.id)
					}
				})
			}
		},
		fetchData() {
			let vm = this

			this.spinner(true)

			let promises = [
				this.fetchTemplateCategories(false),
				this.fetchJobPersonnelRoles(["id","slug","title"]),
				this.fetchUsers([], false, ['first_name', 'last_name'])
			]

			if (this.$store.equipmentTypes.length == 0)
				promises.push(this.fetchEquipmentTypes())

			if (this.$route.params.slug != 'new')
				promises.push(this.fetchTemplateExtended())

			Promise.all(promises).then(responses => {

				if (responses[1].success) {
					vm.personnelRoles = responses[1].data
				}

				if (responses[2].success)
					vm.employees = responses[2].data

				vm.setCategoryFromUrl()

				vm.spinner(false)
			})
		}
	},
	created() {
		let vm = this

		if (this.$route.params.slug == 'new') {
			this.formTemplate = [{
				sectionHeading: "[New Section]",
				fields: [
					{
						label: "[New Field]",
						type: "text"
					}
				]
			}]
			this.activeSectionIndex = 0
			this.template.title = "[New Form Template]"
			this.template.expiry_interval = 0
		}

		this.fetchData()
	}
}
</script>

<style type="text/css" scoped>
	.expiry-info .expiry-info-text {
		display: none;
		cursor: pointer;
	}

	.expiry-info:hover .expiry-info-text {
		display: block;
	}

	.preview-panel {
		margin-top: 20px;
	}

	.preview-panel section {
		border: 1px solid #e0e0e0;
		padding: 10px;
	}

	.preview-panel section h4 {
		text-decoration: none;
	}

	.preview-panel section .section-question {
		padding: 5px;
	}

	.preview-panel section .section-question:nth-child(even) {
		background-color: #f7f8f9;
	}

	.preview-panel section h5 {
		font-size: 12pt;
	}
</style>